import { inject, Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { ApiRouteSearchService } from '../../../api/elite/routes/search.service';
import {
  SideBarButton,
  SideNavItem,
} from '../../../settings/nav-list/recursive-sidebar/class/sidebar';
import { NavButton } from 'src/app/api/backend/types/nav-button';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { HIDDEN_ROUTES } from '../../../settings/routes/other-routes/hidden-routes';
import { searchNavButtons } from '../../../api/backend/types/search-nav-buttons';

type SearchResult = {
  type: string;
  data: NavButton[];
};

const SEARCH_TYPES = {
  ROUTES: 'Rutas',
  WORK_ORDERS: 'Work Orders',
  SALES_ORDERS: 'Sales Orders',
} as const;

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private readonly apiSearchRouter = inject(ApiRouteSearchService);
  private allRutas: NavButton[] = [];
  readonly hiddenRoutes = HIDDEN_ROUTES;

  private _isLoading = new BehaviorSubject<boolean>(false);
  isLoading$ = this._isLoading.asObservable();

  formControl = new FormControl<string>('');

  private _showOptions = new BehaviorSubject<boolean>(false);
  showOptions$ = this._showOptions.asObservable();

  private _searchOptions = new BehaviorSubject<SearchResult[]>([]);

  readonly searchOptions$ = this._searchOptions.asObservable();

  showAutocomplete(val: boolean): void {
    this._showOptions.next(val);
  }

  cargarSidebarRoutes(button: SideBarButton[]): void {
    const items = [...button, ...this.hiddenRoutes];
    this.setRutas(this.flattenNavButtons(items));
  }

  setRutas(data: NavButton[]): void {
    this.allRutas = [...data];
  }
  private _searchType = new BehaviorSubject<string>('');
  searchType$ = this._searchType.asObservable();

  setSearchType(type: string) {
    this._searchType.next(type);
  }

  search$(): Observable<SearchResult[]> {
    const searchTerm$ = this.formControl.valueChanges.pipe(
      startWith(''),
      map((search) => search.trim().toLowerCase()),
      distinctUntilChanged()
    );

    const apiResults$ = combineLatest([searchTerm$, this.searchType$]).pipe(
      debounceTime(200),
      switchMap(([search, type]) => {
        const shouldCallApi = search.length > 0;

        if (!shouldCallApi) {
          this._isLoading.next(false);
          return of([]);
        }

        this._isLoading.next(true);

        return this.getApiSearchResults(search, type).pipe(
          catchError(() => of([])),
          tap(() => this._isLoading.next(false))
        );
      })
    );

    const localResults$ = searchTerm$.pipe(
      map((search) => this.getLocalRoutes(search))
    );

    return combineLatest([localResults$, apiResults$, this.searchType$]).pipe(
      map(([local, api, type]) => {
        const results = [...api, ...local];
        return this.filterByType(results, type);
      }),
      tap((options) => this._searchOptions.next(options))
    );
  }

  private filterByType(options: SearchResult[], type: string): SearchResult[] {
    if (!type) return options;

    return options
      .map((opt) => ({
        ...opt,
        data: opt.type === type ? opt.data : [],
      }))
      .filter((opt) => opt.data.length > 0);
  }

  private filterItem = (item: NavButton, search: string): boolean => {
    if (!search?.trim()) {
      return true;
    }

    const searchTerms = search.trim().toLowerCase().split(/\s+/);

    const searchableValue = `${item.title} ${item.chips.join(
      ' '
    )}`.toLowerCase();

    if (searchTerms.length === 1) {
      return searchableValue.includes(search);
    }

    return searchTerms.every((term) => searchableValue.includes(term));
  };

  private getLocalRoutes(search: string): SearchResult[] {
    const filteredRoutes = this.allRutas.filter((route) =>
      this.filterItem(route, search)
    );

    return [
      {
        type: SEARCH_TYPES.ROUTES,
        data: filteredRoutes,
      },
    ];
  }

  private getApiSearchResults(
    search: string,
    type: string
  ): Observable<SearchResult[]> {
    return this.apiSearchRouter.searchV3(search, type);
  }

  private skipApiSearch(search: string): boolean {
    return !search || search.split(/\s+/).length > 1;
  }

  private flattenNavButtons(
    buttons: SideBarButton[],
    parentChips: string[] = [],
    inheritedUrl: string = '.',
    icon: string = 'alt_route',
    color: string = ''
  ): NavButton[] {
    const result: NavButton[] = [];

    const getUrl = (item: SideNavItem, extraUrl: string): string => {
      return item.overWriteUrl ?? `${extraUrl}${item.url ?? ''}`;
    };

    const recurse = (
      btns: SideBarButton[],
      chipsSeed: string[],
      currentUrl: string
    ): void => {
      for (const btn of btns) {
        const currentChip = btn.data.name;
        const newChips = [...chipsSeed, currentChip];
        const nextExtraUrl = btn.data.appendUrl ?? currentUrl;

        if (btn.butons?.length) {
          recurse(btn.butons, newChips, nextExtraUrl);
          continue;
        }
        result.push({
          title: currentChip,
          icon: btn.icon ?? icon,
          url: getUrl(btn.data, nextExtraUrl),
          chips: chipsSeed,
          color,
        });
      }
    };

    recurse(buttons, parentChips, inheritedUrl);
    return result;
  }
}
